import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosInstance from '../../axiosConfig';
import { colors } from '../../colors';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';

const CreateSurvey = () => {
    const [surveyData, setSurveyData] = useState({
        surveyName: '',
        surveyDescription: '',
        universityName: '',
        logo: null,
        uniqueLinksCount: '',
        validTill: null,
        templateId: '',
    });

    const [templates, setTemplates] = useState([]); // Dropdown data for templates
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const [notification, setNotification] = useState(null);

    // Fetch available templates for the dropdown
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await axiosInstance.get('/api/v2/templates');
                setTemplates(response.data);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };

        fetchTemplates();
    }, []);

    // Handle form data change
    const handleSurveyDataChange = (e) => {
        const { name, value } = e.target;
        setSurveyData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Handle logo file selection
    const handleLogoChange = (e) => {
        setSurveyData((prevData) => ({
            ...prevData,
            logo: e.target.files[0],
        }));
    };

    // Handle date change for "Valid Till"
    const handleValidTillChange = (newDate) => {
        setSurveyData((prevData) => ({
            ...prevData,
            validTill: newDate,
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        Object.entries(surveyData).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            const response = await axiosInstance.post('/api/surveys', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status === 200) {
                setNotification({ message: 'Survey Created Successfully!', type: 'success' });
                setTimeout(() => {
                    setNotification(null);
                    navigate('/admin/dashboard', {
                        state: { view: 'ViewSurvey' },
                    });
                }, 2000);
                // navigate('/admin/dashboard'); // Navigate to dashboard after success
            }
        } catch (error) {
            console.error("Error Saving Template:", error);
            setNotification({
                message: 'Failed to create SUrvey. Please try again.',
                type: 'error',
            });
            setTimeout(() => setNotification(null), 4000);
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            {/* Page Title */}
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 'bold',
                    color: colors.primaryApplicationText,
                    textAlign: 'center',
                    mb: 4,
                }}
            >
                Create Survey
            </Typography>
            
            {/* Form Container */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    backgroundColor: '#ffffff',
                    padding: '24px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                    overflow: 'visible',
                }}
            >
                <Grid container spacing={3}>
                    {/* Survey Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Survey Name"
                            name="surveyName"
                            value={surveyData.surveyName}
                            onChange={handleSurveyDataChange}
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                                '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                            }}
                        />
                    </Grid>

                    {/* Survey Description */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Survey Description"
                            name="surveyDescription"
                            value={surveyData.surveyDescription}
                            onChange={handleSurveyDataChange}
                            fullWidth
                            multiline
                            rows={3}
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                                '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                            }}
                        />
                    </Grid>

                    {/* University Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="University Name"
                            name="universityName"
                            value={surveyData.universityName}
                            onChange={handleSurveyDataChange}
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                                '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                            }}
                        />
                    </Grid>

                    {/* Logo Upload */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            type="file"
                            inputProps={{ accept: 'image/*' }}
                            onChange={handleLogoChange}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                            }}
                        />
                        <Typography variant="caption" sx={{ marginTop: '4px', display: 'block', color: '#666' }}>
                            Accepted formats: PNG, JPG
                        </Typography>
                    </Grid>

                    {/* Number of Unique Links */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Number of Unique Links"
                            name="uniqueLinksCount"
                            type="number"
                            value={surveyData.uniqueLinksCount}
                            onChange={handleSurveyDataChange}
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                                '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                            }}
                        />
                    </Grid>

                    {/* Valid Till */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Valid Till"
                                value={surveyData.validTill}
                                onChange={handleValidTillChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: colors.primaryBannerBlue,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primaryApplicationText,
                                                },
                                            },
                                            '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Template Selection Dropdown */}
                    <Grid item xs={12}>
                        <TextField
                            select
                            label="Select Template"
                            name="templateId"
                            value={surveyData.templateId}
                            onChange={handleSurveyDataChange}
                            fullWidth
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: colors.primaryBannerBlue,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: colors.primaryApplicationText,
                                    },
                                },
                                '& .MuiInputLabel-root': { color: colors.primaryApplicationText },
                            }}
                        >
                            {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                {/* Submit Button */}
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            backgroundColor: colors.primaryBannerBlue,
                            color: '#ffffff',
                            padding: '10px 20px',
                            '&:hover': {
                                backgroundColor: colors.primaryApplicationText,
                            },
                        }}
                    >
                        Create Survey
                    </Button>
                </Box>
            </Box>

            {/* Error/Feedback Message */}
            {message && (
                <Typography variant="body2" color="error" sx={{ mt: 3, textAlign: 'center' }}>
                    {message}
                </Typography>
            )}

            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </Container>
    );
};

export default CreateSurvey;
